/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useState, useContext, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { auth, db } from '../config/firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingProduct, setPendingProduct] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Track auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await loadUserWishlist(currentUser.uid);
      } else {
        loadGuestWishlist();
      }
    });
    return () => unsubscribe();
  }, []);

  // Load guest wishlist from localStorage (only for display, not for adding)
  const loadGuestWishlist = () => {
    try {
      const saved = localStorage.getItem('guest_wishlist');
      if (saved) {
        setWishlistItems(JSON.parse(saved));
      } else {
        setWishlistItems([]);
      }
    } catch (error) {
      console.error('Error loading guest wishlist:', error);
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Load user wishlist from Firestore
  const loadUserWishlist = async (userId) => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      let wishlistData = [];
      let wishlistAddedAt = {};

      if (userDoc.exists()) {
        const data = userDoc.data();
        wishlistData = data.wishlist || [];

        // Handle both old (array of strings) and new (array of objects) format
        if (wishlistData.length > 0 && typeof wishlistData[0] === 'object') {
          const ids = wishlistData.map(item => item.id);
          wishlistAddedAt = wishlistData.reduce((acc, item) => {
            acc[item.id] = item.addedAt;
            return acc;
          }, {});
          wishlistData = ids;
        }
      }

      // Fetch full product details
      const products = await fetchWishlistProducts(wishlistData);
      const productsWithAddedAt = products.map(product => ({
        ...product,
        addedAt: wishlistAddedAt[product.id] || new Date().toISOString()
      }));

      setWishlistItems(productsWithAddedAt);
    } catch (error) {
      console.error('Error loading user wishlist:', error);
      setWishlistItems([]);
      showSnackbar('Failed to load wishlist', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Query by document ID (__name__) instead of a non‑existent 'id' field
  const fetchWishlistProducts = async (productIds) => {
    if (!productIds.length) return [];

    try {
      const productsRef = collection(db, 'products');
      const products = [];
      const batchSize = 30;

      for (let i = 0; i < productIds.length; i += batchSize) {
        const batch = productIds.slice(i, i + batchSize);
        // Use __name__ to query by Firestore document ID
        const q = query(productsRef, where('__name__', 'in', batch));
        const snapshot = await getDocs(q);
        const foundIds = new Set();

        snapshot.docs.forEach(doc => {
          foundIds.add(doc.id);
          products.push({
            id: doc.id,
            ...doc.data()
          });
        });

        // Fallback for products that exist in wishlist but not in products collection
        batch.forEach(id => {
          if (!foundIds.has(id)) {
            console.warn(`Product ${id} not found in products – keeping placeholder`);
            products.push({
              id: id,
              name: 'Unavailable Product',
              price: 0,
              images: [],
              isPlaceholder: true
            });
          }
        });
      }
      return products;
    } catch (error) {
      console.error('Error fetching wishlist products:', error);
      return [];
    }
  };

  // Save guest wishlist to localStorage (only used for removal and merge)
  const saveGuestWishlist = (items) => {
    localStorage.setItem('guest_wishlist', JSON.stringify(items));
  };

  // Show auth modal for guest users
  const showAuthRequiredModal = (product) => {
    setPendingProduct(product);
    setShowAuthModal(true);
    showSnackbar('Please sign in to add items to your wishlist', 'warning');
  };

  // Add to wishlist – guests see auth modal, only logged‑in users modify
  const addToWishlist = async (product) => {
    if (!user) {
      showAuthRequiredModal(product);
      return false;
    }

    const productId = product.id;
    if (!productId) {
      showSnackbar('Invalid product', 'error');
      return false;
    }

    if (isInWishlist(productId)) {
      showSnackbar(`${product.name} is already in your wishlist`, 'info');
      return false;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const wishlistItem = {
        id: productId,
        addedAt: new Date().toISOString()
      };
      await updateDoc(userRef, {
        wishlist: arrayUnion(wishlistItem)
      });
      setWishlistItems(prev => [{ ...product, addedAt: wishlistItem.addedAt }, ...prev]);
      showSnackbar(`${product.name} added to wishlist`, 'success');
      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      if (error.code === 'not-found') {
        try {
          const userRef = doc(db, 'users', user.uid);
          await setDoc(userRef, {
            wishlist: [{ id: productId, addedAt: new Date().toISOString() }],
            email: user.email,
            createdAt: new Date().toISOString()
          }, { merge: true });
          setWishlistItems(prev => [{ ...product, addedAt: new Date().toISOString() }, ...prev]);
          showSnackbar(`${product.name} added to wishlist`, 'success');
          return true;
        } catch (createError) {
          console.error('Error creating user wishlist:', createError);
        }
      }
      showSnackbar('Failed to add to wishlist', 'error');
      return false;
    }
  };

  // Remove from wishlist – guests can remove from localStorage, logged‑in users remove from Firestore
  const removeFromWishlist = async (productId) => {
    const product = wishlistItems.find(item => item.id === productId);
    if (!user) {
      const updated = wishlistItems.filter(item => item.id !== productId);
      setWishlistItems(updated);
      saveGuestWishlist(updated);
      showSnackbar(`${product?.name || 'Product'} removed from wishlist`, 'info');
      return;
    }

    try {
      const userRef = doc(db, 'users', user.uid);
      const wishlistItemToRemove = {
        id: productId,
        addedAt: product?.addedAt || new Date().toISOString()
      };
      await updateDoc(userRef, {
        wishlist: arrayRemove(wishlistItemToRemove)
      });
      setWishlistItems(prev => prev.filter(item => item.id !== productId));
      showSnackbar(`${product?.name || 'Product'} removed from wishlist`, 'info');
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showSnackbar('Failed to remove from wishlist', 'error');
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.id === productId);
  };

  const getWishlistCount = () => wishlistItems.length;

  const clearWishlist = async () => {
    if (!user) {
      setWishlistItems([]);
      saveGuestWishlist([]);
      showSnackbar('Wishlist cleared', 'info');
      return;
    }
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { wishlist: [] });
      setWishlistItems([]);
      showSnackbar('Wishlist cleared', 'info');
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      showSnackbar('Failed to clear wishlist', 'error');
    }
  };

  const getWishlistSortedByDate = (newestFirst = true) => {
    return [...wishlistItems].sort((a, b) => {
      const dateA = new Date(a.addedAt).getTime() || 0;
      const dateB = new Date(b.addedAt).getTime() || 0;
      return newestFirst ? dateB - dateA : dateA - dateB;
    });
  };

  // Merge guest wishlist into user account after login
  const mergeGuestWishlistWithUser = async () => {
    if (!user) return;
    const guestRaw = localStorage.getItem('guest_wishlist');
    if (!guestRaw) return;
    const guestItems = JSON.parse(guestRaw);
    if (guestItems.length === 0) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      let existingMap = new Map();

      if (userDoc.exists()) {
        const existing = userDoc.data().wishlist || [];
        existing.forEach(item => {
          const id = typeof item === 'object' ? item.id : item;
          const addedAt = typeof item === 'object' ? item.addedAt : new Date().toISOString();
          existingMap.set(id, { id, addedAt });
        });
      }

      // Add guest items if not already present
      guestItems.forEach(guest => {
        if (!existingMap.has(guest.id)) {
          existingMap.set(guest.id, {
            id: guest.id,
            addedAt: guest.addedAt || new Date().toISOString()
          });
        }
      });

      const merged = Array.from(existingMap.values());
      await setDoc(userRef, {
        wishlist: merged,
        email: user.email,
        lastLogin: new Date().toISOString()
      }, { merge: true });

      localStorage.removeItem('guest_wishlist');
      await loadUserWishlist(user.uid);
      showSnackbar('Wishlist synced successfully', 'success');
    } catch (error) {
      console.error('Error merging wishlists:', error);
    }
  };

  // Sync when user logs in
  useEffect(() => {
    if (user) {
      mergeGuestWishlistWithUser();
    }
  }, [user]);

  const value = {
    wishlistItems,
    loading,
    user,
    showAuthModal,
    setShowAuthModal,
    pendingProduct,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getWishlistCount,
    clearWishlist,
    mergeGuestWishlistWithUser,
    showSnackbar,
    getWishlistSortedByDate,
  };

  return (
    <>
      <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};